/**
 * Economía de Combos C1A (cliente) — espejo determinístico del dominio backend.
 * Margen sobre precio de venta. No escribe listas de precios.
 */
import { resolveProductSalePrice, roundProductPrice } from './listasPreciosLabels';

export function roundMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

export function resolveProductoCosto(producto) {
  const n = Number(producto?.precio_costo ?? producto?.costo ?? producto?.costo_unitario);
  if (!Number.isFinite(n) || n <= 0) return null;
  return roundMoney(n);
}

export function calcularEconomiaLocal({
  componentes = [],
  precioVenta,
  productosById = {},
  listaKey = 'mayorista'
} = {}) {
  const detalle = [];
  let costoTotal = 0;
  let sumaPreciosIndividuales = 0;
  let missingCost = false;
  let invalidQty = false;

  for (const c of componentes) {
    const productId = c.productId;
    const cantidad = Number(c.cantidad);
    if (!productId || !Number.isFinite(cantidad) || cantidad <= 0) {
      invalidQty = true;
      continue;
    }
    const producto = productosById[productId] || null;
    const costoUnitario = resolveProductoCosto(producto);
    const precioUnitario = producto
      ? resolveProductSalePrice(producto, listaKey)
      : 0;
    if (costoUnitario === null) missingCost = true;
    else costoTotal = roundMoney(costoTotal + costoUnitario * cantidad);
    sumaPreciosIndividuales = roundMoney(sumaPreciosIndividuales + precioUnitario * cantidad);
    detalle.push({
      productId,
      cantidad,
      costoUnitario,
      precioUnitario,
      nombre: producto?.nombre || c.nombre_snapshot || productId,
      codigo: producto?.codigo || c.codigo_snapshot || ''
    });
  }

  const precio = Number(precioVenta);
  const precioOk = Number.isFinite(precio) && precio > 0 ? roundProductPrice(precio) : null;
  const costoFinal = missingCost || invalidQty || detalle.length === 0 ? null : costoTotal;
  const ganancia =
    precioOk !== null && costoFinal !== null ? roundMoney(precioOk - costoFinal) : null;
  const margenPct =
    precioOk !== null && precioOk > 0 && ganancia !== null
      ? roundMoney((ganancia / precioOk) * 100)
      : null;
  const descuentoAbsoluto =
    precioOk !== null ? roundMoney(sumaPreciosIndividuales - precioOk) : null;
  const descuentoPct =
    sumaPreciosIndividuales > 0 && descuentoAbsoluto !== null
      ? roundMoney((descuentoAbsoluto / sumaPreciosIndividuales) * 100)
      : null;

  const activationErrors = [];
  if (detalle.length === 0) {
    activationErrors.push({ code: 'COMBO_NO_COMPONENTES', message: 'Agregá al menos un producto' });
  }
  if (invalidQty) {
    activationErrors.push({ code: 'COMBO_CANTIDAD_INVALIDA', message: 'Hay cantidades inválidas' });
  }
  if (missingCost) {
    activationErrors.push({
      code: 'COMBO_COSTO_FALTANTE',
      message: 'Falta costo en uno o más componentes'
    });
  }
  if (precioOk === null) {
    activationErrors.push({
      code: 'COMBO_PRECIO_INVALIDO',
      message: 'Definí un precio de venta válido'
    });
  } else if (costoFinal !== null && precioOk < costoFinal) {
    activationErrors.push({
      code: 'COMBO_PRECIO_BAJO_COSTO',
      message: 'El precio es menor al costo total'
    });
  }
  if (ganancia !== null && ganancia < 0) {
    activationErrors.push({
      code: 'COMBO_GANANCIA_NEGATIVA',
      message: 'La ganancia es negativa'
    });
  }
  if (margenPct !== null && margenPct < 0) {
    activationErrors.push({
      code: 'COMBO_MARGEN_NEGATIVO',
      message: 'El margen es negativo'
    });
  }

  return {
    detalle,
    precioVenta: precioOk,
    costoTotal: costoFinal,
    ganancia,
    margenPct,
    sumaPreciosIndividuales,
    descuentoAbsoluto,
    descuentoPct,
    activationErrors,
    canActivate: activationErrors.length === 0
  };
}

export function escenariosRecomendador({
  sumaPreciosIndividuales = 0,
  costoTotal = null,
  descuentos = [0, 5, 10, 15]
} = {}) {
  const suma = roundMoney(sumaPreciosIndividuales);
  const costo =
    costoTotal != null && Number.isFinite(Number(costoTotal))
      ? roundMoney(Number(costoTotal))
      : null;
  return descuentos.map((descuentoPct) => {
    const precioPropuesto = roundMoney(suma * (1 - descuentoPct / 100));
    const ganancia = costo !== null ? roundMoney(precioPropuesto - costo) : null;
    const margenPct =
      precioPropuesto > 0 && ganancia !== null
        ? roundMoney((ganancia / precioPropuesto) * 100)
        : null;
    return {
      descuentoPct,
      precioPropuesto,
      descuentoAbsoluto: roundMoney(suma - precioPropuesto),
      costoTotal: costo,
      ganancia,
      margenPct,
      viable:
        costo !== null &&
        ganancia !== null &&
        ganancia >= 0 &&
        margenPct !== null &&
        margenPct >= 0
    };
  });
}
